import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Check, Sparkles } from "lucide-react";
import type { Plan } from "@/hooks/useAppSettings";

const defaultPlans: Plan[] = [
  { id: "free", name: "Free", price: "$0", period: "forever", features: ["5 generations / day", "Powered by Gemini 3 Flash"] },
  { id: "plus", name: "Plus", price: "$9", period: "/month", features: ["120 generations / month", "Powered by GPT-5 Mini"] },
  { id: "pro", name: "Pro", price: "$19", period: "/month", features: ["300 generations / month", "Powered by GPT-5 (top tier)"], highlight: true },
];

export default function AdminPlans() {
  const [plans, setPlans] = useState<Plan[]>(defaultPlans);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "plans")
        .maybeSingle();
      if (Array.isArray(data?.value) && (data!.value as any[]).length) {
        setPlans(data!.value as Plan[]);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Plan catalog</CardTitle>
          <CardDescription>
            Read-only view of the plans shown to customers. Plan tiers and limits are
            controlled by Paddle and the billing logic in the backend.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {plans.map((p, i) => (
              <Card
                key={i}
                className={
                  p.highlight
                    ? "border-primary shadow-glow bg-gradient-card relative"
                    : "bg-gradient-card border-border/50 relative"
                }
              >
                {p.highlight && (
                  <Badge className="absolute -top-2 right-4 gap-1">
                    <Sparkles className="h-3 w-3" /> Recommended
                  </Badge>
                )}
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="secondary" className="font-mono text-[10px]">
                      {p.id}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg">{p.name}</CardTitle>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold">{p.price}</span>
                    <span className="text-sm text-muted-foreground">{p.period}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {p.features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full mt-4"
                    variant={p.highlight ? "default" : "outline"}
                    disabled
                  >
                    {p.id === "free" ? "Current plan" : `Choose ${p.name}`}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
